/**
 * Persist the React Query cache across reloads/app restarts.
 *
 * WHY: the cache lives in RAM, so every app open started from an EMPTY cache —
 * each page's first visit showed skeletons for a full API round-trip even when
 * the user had seen that exact data seconds before the reload. Persisting the
 * dehydrated cache means the app opens showing the last-known data instantly,
 * while every mounted query still revalidates in the background (hydrated
 * entries keep their old `dataUpdatedAt`, so they are stale on arrival).
 *
 * Hand-rolled on `dehydrate`/`hydrate` (bundled with @tanstack/react-query) —
 * no extra dependency. Only SUCCESSFUL queries are dehydrated (library
 * default), so errors and pending states are never revived.
 *
 * SECURITY: the snapshot contains business data, so it is wiped whenever the
 * session ends — `clearPrivateCaches` (logout AND terminal 401) calls
 * `clearPersistedQueries`. It lives in `localStorage`, which already holds the
 * auth tokens in browser mode (token-storage.ts), so it introduces no new
 * exposure class. Snapshots expire after MAX_AGE_MS regardless.
 */

import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "fad.querycache.v1";
/** Snapshots older than this are discarded on restore (stale business data). */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Collapse bursts of cache events into one write. */
const WRITE_DEBOUNCE_MS = 1_000;
/** Refuse to write snapshots that could blow the localStorage quota. */
const MAX_SNAPSHOT_BYTES = 4 * 1024 * 1024;

interface PersistedSnapshot {
  v: 1;
  savedAt: number;
  state: unknown; // DehydratedState
}

/** `localStorage` if present (guarded for the node test/SSR environments). */
function storage(): Storage | null {
  try {
    return typeof globalThis !== "undefined" && globalThis.localStorage ? globalThis.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Hydrate the client from the persisted snapshot, if any. Call ONCE at startup,
 * before the first render. Corrupted, versioned-out or expired snapshots are
 * discarded silently — restoring is best-effort and must never break boot.
 */
export function restorePersistedQueries(client: QueryClient): void {
  const ls = storage();
  if (!ls) return;
  try {
    const raw = ls.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as PersistedSnapshot | null;
    if (!parsed || parsed.v !== 1 || typeof parsed.savedAt !== "number") {
      ls.removeItem(STORAGE_KEY);
      return;
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      ls.removeItem(STORAGE_KEY);
      return;
    }
    hydrate(client, parsed.state);
  } catch {
    // Unreadable snapshot — drop it so it cannot fail every boot.
    try {
      ls.removeItem(STORAGE_KEY);
    } catch {
      // storage gone mid-flight — nothing left to clean
    }
  }
}

/**
 * Start mirroring the query cache to storage (debounced). Returns a stop
 * function. Writes are best-effort: quota errors and serialization failures
 * are swallowed — persistence must never break the app.
 */
export function startQueryPersistence(client: QueryClient): () => void {
  const ls = storage();
  if (!ls) return () => undefined;

  let timer: ReturnType<typeof setTimeout> | null = null;

  const write = () => {
    timer = null;
    try {
      const snapshot: PersistedSnapshot = { v: 1, savedAt: Date.now(), state: dehydrate(client) };
      const raw = JSON.stringify(snapshot);
      if (raw.length > MAX_SNAPSHOT_BYTES) return; // oversized — skip this write
      ls.setItem(STORAGE_KEY, raw);
    } catch {
      // QuotaExceeded / serialization failure — skip this write
    }
  };

  const unsubscribe = client.getQueryCache().subscribe(() => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(write, WRITE_DEBOUNCE_MS);
  });

  return () => {
    unsubscribe();
    if (timer) clearTimeout(timer);
  };
}

/** Drop the persisted snapshot (called whenever the session ends). */
export function clearPersistedQueries(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — nothing persisted anyway
  }
}
